/** Text Agent — monitor + queue management (Phase A → 13). Full visibility
 * into v1's agent, PLUS the safe half of the console: edit drafts, skip
 * queue items, approve/pause A/B scripts — all whole-AGENT-blob writes
 * through the Phase 13 door, always mutating a FRESH copy by item identity.
 * SENDING happens in the cron (server_code/agent_runner.py): replies send on
 * their own when their toggle is on; cold texts send on their own only while
 * "Auto outreach" is on, otherwise they wait here for "Send all approved". */
import { useMemo, useRef, useState } from "react";
import {
  AGENT_KIND_LABEL, AGENT_MSG, agentAddVariantIn, agentDelVariantIn, agentEditDraftIn, agentEditVariantIn,
  agentInSendWindow, agentItemId, agentQueueSummary, agentSkipItemIn, agentApproveItemIn, agentToggleVariantIn, agentVariantId,
  agentSetRepAutoIn, agentSetConvIn, agentSetReengageIn, agentSetAutoOutreachIn, agentApproveAllPendingIn,
  narrowAgent, type AgentItemId,
} from "./api";

const STATUS_ORDER = ["pending", "approved", "drafting", "scheduled", "error", "sent"];
const STATUS_HUE: Record<string, string> = {
  pending: "blue", approved: "violet", drafting: "amber", scheduled: "violet", error: "red", sent: "emerald",
};

const when = (t: number | null): string =>
  t ? new Date(t).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

export function AgentPage({
  agentRaw,
  onOpenLead,
  fetchFresh,
  onPutAgent,
  ptextOn = false,
  onTogglePtext,
}: {
  agentRaw: unknown;
  onOpenLead: (address: string) => void;
  /** fresh AGENT value from the DB — the base of every read-modify-write */
  fetchFresh: () => Promise<Record<string, unknown> | null>;
  /** optimistic paint + outbox enqueue of the whole-blob settings put */
  onPutAgent: (blob: Record<string, unknown>) => void;
  /** personal texter (non-Zillow rules, Tom's number) — separate PTEXT key */
  ptextOn?: boolean;
  onTogglePtext?: (on: boolean) => void;
}) {
  const A = useMemo(() => narrowAgent(agentRaw), [agentRaw]);
  const sum = useMemo(() => agentQueueSummary(A.queue), [A.queue]);
  const windowOpen = agentInSendWindow(Date.now());
  const [filter, setFilter] = useState<string>("live");
  const [showQueue, setShowQueue] = useState(false); // v1: queue collapsed behind Show
  const [editing, setEditing] = useState<{ id: AgentItemId; text: string } | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [armSend, setArmSend] = useState(false);   // two-step confirm for the batch send
  // cumulative working copy: offline bursts build on each other, not on a
  // stale fetch (same discipline as the comps/arv persist paths)
  const working = useRef<Record<string, unknown> | null>(null);

  const act = async (mutate: (base: Record<string, unknown>) => Record<string, unknown> | null) => {
    const base = working.current ?? (await fetchFresh()) ?? (agentRaw !== null && typeof agentRaw === "object" && !Array.isArray(agentRaw) ? (agentRaw as Record<string, unknown>) : null);
    if (!base) {
      setNote(AGENT_MSG.gone);
      return;
    }
    const next = mutate(base);
    if (!next) {
      working.current = base; // keep the fresh copy — the UI repaints from it
      onPutAgent(base);
      setNote(AGENT_MSG.gone);
      return;
    }
    working.current = next;
    onPutAgent(next);
    setNote(AGENT_MSG.saved);
  };

  const rows = useMemo(() => {
    const base = filter === "live" ? A.queue.filter((q) => q.status !== "sent") : filter === "all" ? A.queue : A.queue.filter((q) => q.status === filter);
    return [...base].sort((a, b) =>
      STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) || (b.added || 0) - (a.added || 0));
  }, [A.queue, filter]);

  const approved = A.variants.filter((v) => v.approved);
  // stats table shows only variants with real sends (v1 does the same); the full
  // editable list below covers every variant, sent or not
  const variantStats = useMemo(() => A.variants.filter((v) => v.sent > 0), [A.variants]);
  // campaign <select> options: every camp the variants actually use, plus the
  // catch-all "default" (rendered "General") — camp names don't live in the blob
  const campOptions = useMemo(() => {
    // "followup" is always offered even before any script lives there — the
    // re-engage sweep reads that campaign for second-touch wording, and a
    // bucket you can't select can never get its first script (Tom asked what
    // a followup campaign IS, 2026-08-10 — partly because it couldn't exist).
    const set = new Set<string>(["default", "followup"]);
    for (const v of A.variants) if (v.camp) set.add(v.camp);
    return [...set];
  }, [A.variants]);

  if (A.queue.length === 0 && A.variants.length === 0) {
    return (
      <div className="agent">
        <div className="empty gate">
          <strong>No agent activity yet.</strong>
          <span>The Text Agent's queue and scripts live in the shared settings. The drafting engine isn't running yet in v2 — queue items appear here once it does.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="agent">
      <header className="bar">
        <span className={"windowpill" + (windowOpen ? " open" : "")}>
          {windowOpen ? "Send window OPEN" : "Quiet hours"} · 9 AM–8 PM ET
        </span>
        <span className="count">
          {sum.pending} awaiting approval · {sum.scheduled} scheduled · {sum.drafting} drafting · {sum.error} blocked · {sum.sent.toLocaleString()} sent
        </span>
        {/* LIVE (2026-08-07, Tom). Two taps, and it only MARKS: pending drafts
            become status "approved"; the cron sends them within ~15 min through
            every gate (window fail-closed, Zillow fence, re-intro block,
            spacing). Nothing sends from this click handler. */}
        {armSend ? (
          <>
            <button className="btn slim primary" onClick={() => { setArmSend(false); void act((b) => agentApproveAllPendingIn(b)); }}>
              ✓ Confirm — send {sum.pending - sum.held} on the next cron pass
            </button>
            <button className="btn slim" onClick={() => setArmSend(false)}>Cancel</button>
          </>
        ) : (
          <button
            className="btn slim"
            disabled={sum.pending - sum.held <= 0}
            title={sum.pending - sum.held > 0 ? "Approve every pending draft — the cron sends them through all the gates" : (sum.held ? "Only parked (Dead Deal) drafts are pending — approve those one at a time" : "No pending drafts to approve")}
            onClick={() => setArmSend(true)}
          >▶ Send all approved ({sum.pending - sum.held})</button>
        )}
      </header>
      {note ? <div className="cmpnote agentnote">{note}</div> : null}

      <div className="inteltiles agenttiles">
        <div className="cstat blue"><strong>{A.textedCount.toLocaleString()}</strong><span>Numbers texted (anti-spam memory)</span></div>
        <div className="cstat violet"><strong>{A.gkSent.toLocaleString()}</strong><span>Gatekeeper texts</span></div>
        <div className="cstat emerald"><strong>{A.gkReplies.toLocaleString()}</strong><span>Gatekeeper replies</span></div>
        <div className="cstat amber"><strong>{A.gkOpps.toLocaleString()}</strong><span>Gatekeeper opportunities</span></div>
      </div>

      <div className="dcard">
        <div className="dhead">
          <h2>Queue <span className="qcount">{A.queue.length.toLocaleString()}</span></h2>
          <span className="agentfilters">
            <span className="agentaddleads sub">Add leads: select them in Properties → "Queue for texting"</span>
            {showQueue ? ["live", "sent", "error", "all"].map((f) => (
              <button key={f} className={"vtab" + (filter === f ? " on" : "")} onClick={() => setFilter(f)}>
                {f === "live" ? "Live" : f === "sent" ? "Sent" : f === "error" ? "Blocked" : "All"}
              </button>
            )) : null}
            <button className="vtab plus" onClick={() => setShowQueue((v) => !v)}>{showQueue ? "Hide" : "Show"}</button>
          </span>
        </div>
        {!showQueue ? (
          <div className="agentqcollapsed sub">
            {sum.pending.toLocaleString()} pending approval · {sum.scheduled} scheduled · {sum.drafting} drafting · {sum.sent.toLocaleString()} sent · {sum.error} error{sum.error === 1 ? "" : "s"} — messages hidden, hit <b>Show</b> to review.
          </div>
        ) : null}
        {showQueue && rows.length === 0 ? <div className="sub none">Nothing here.</div> : null}
        {showQueue ? rows.slice(0, 80).map((q, i) => {
          const id = agentItemId(q);
          const isEditing =
            editing !== null && editing.id.a === id.a && editing.id.num === id.num && editing.id.kind === id.kind && editing.id.added === id.added;
          const live = q.status === "pending" || q.status === "scheduled" || q.status === "drafting" || q.status === "error";
          return (
            <div key={q.address + q.added + i} className="agentrow">
              <span className={"rolepill role-" + (STATUS_HUE[q.status] ?? "slate")}>{q.status}</span>
              <div className="agentbody">
                <button className="agentaddr" onClick={() => onOpenLead(q.address)}>{q.address.split(",")[0] || q.num}</button>
                <span className="sub">
                  {AGENT_KIND_LABEL[q.kind] ?? q.kind} · {q.camp}
                  {q.status === "scheduled" ? ` · fires ${when(q.sendAt)}` : q.status === "sent" ? ` · sent ${when(q.sentAt)}` : ` · queued ${when(q.added)}`}
                </span>
                {isEditing ? (
                  <div className="agentedit">
                    <textarea
                      rows={3}
                      value={editing.text}
                      maxLength={320}
                      onChange={(e) => setEditing({ id, text: e.target.value })}
                    />
                    <div className="agenteditacts">
                      <button
                        className="btn slim primary"
                        onClick={() => {
                          const t = editing.text.trim();
                          setEditing(null);
                          if (t && t !== q.draft) void act((b) => agentEditDraftIn(b, id, t));
                        }}
                      >
                        Save draft
                      </button>
                      <button className="btn slim" onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    {q.draft && q.status !== "sent" ? <span className="agentdraft">{q.draft}</span> : null}
                    {q.err ? <span className="agenterr">{q.err}</span> : null}
                    {q.flag === "dead_deal" && q.status === "pending" ? (
                      <span className="agenterr">
                        ⛔ Parked — this lead is marked Dead Deal, so the robot won't reply on its own and
                        "Send all approved" skips it. Skip it, or use "Approve anyway" to send this one reply.
                      </span>
                    ) : q.note && q.status === "pending" ? <span className="sub">{q.note}</span> : null}
                  </>
                )}
              </div>
              {live && !isEditing ? (
                <span className="agentacts">
                  <button className="btn slim" onClick={() => setEditing({ id, text: q.draft })}>✎ Edit</button>
                  <button className="btn slim" onClick={() => void act((b) => agentSkipItemIn(b, id))}>Skip</button>
                  {q.flag && q.status === "pending" ? (
                    <button className="btn slim" onClick={() => void act((b) => agentApproveItemIn(b, id))}>Approve anyway</button>
                  ) : null}
                  <button className="btn slim" disabled title={AGENT_MSG.sendPhaseB}>Send</button>
                </span>
              ) : null}
            </div>
          );
        }) : null}
        {showQueue && rows.length > 80 ? <div className="sub none">…and {(rows.length - 80).toLocaleString()} more</div> : null}
      </div>

      <div className="dcard">
        <div className="dhead">
          <h2>Opener scripts (A/B)</h2>
          <span className="agentvartools">
            <span className={"vbadge" + (approved.length ? " ok" : " warn")}>{approved.length} approved of {A.variants.length}</span>
            <button
              className="btn slim"
              onClick={() => void act((b) => agentAddVariantIn(b, { id: agentVariantId(), text: "", camp: "default", approved: false }))}
            >
              + Add variant
            </button>
            <button className="btn slim" disabled title="Generating new drafts runs in the live send session">✨ Generate 10 drafts</button>
          </span>
        </div>
        <p className="agentvarnote">
          Approve the ones you like and edit freely — every outgoing text is a unique AI re-wording of ONE approved variant from the lead's campaign. Tokens <code>{"{name}"}</code> <code>{"{address}"}</code> <code>{"{me}"}</code> stay literal.
        </p>

        {variantStats.length ? (
          <div className="inteltablewrap">
            <table className="inteltable">
              <thead>
                <tr><th>Script</th><th>Campaign</th><th>Sent</th><th>Replies</th><th>Reply %</th><th>Positive</th><th>Negative</th></tr>
              </thead>
              <tbody>
                {variantStats.map((v) => (
                  <tr key={v.id || v.text.slice(0, 20)} className={v.approved ? "" : "vdim"}>
                    <td className="vtext" title={v.text}>{v.approved ? "" : "⏸ "}{v.text.slice(0, 80) || "(empty)"}{v.text.length > 80 ? "…" : ""}</td>
                    <td>{v.camp === "default" ? "General" : v.camp}</td>
                    <td>{v.sent}</td>
                    <td>{v.replies}</td>
                    <td className="ratio">{v.sent ? Math.round((v.replies / v.sent) * 100) + "%" : "—"}</td>
                    <td className="abpos">{v.pos || 0}</td>
                    <td className="abneg">{v.neg || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <div className="agentvarlist">
          {A.variants.length === 0 ? (
            <div className="agentvarempty">No variants yet — hit <b>+ Add variant</b>, then approve your favorites.</div>
          ) : (
            A.variants.map((v, i) => {
              const editable = !!v.id;
              return (
                <div key={v.id || "row" + i} className={"agentvarrow" + (v.approved ? " on" : "")}>
                  <input
                    type="checkbox"
                    className="agentvarcb"
                    checked={v.approved}
                    disabled={!editable}
                    title={editable ? (v.approved ? "Approved — uncheck to pause this script" : "Approve this script for the A/B rotation") : "New script — v1 assigns it an id on its next sync"}
                    onChange={(e) => void act((b) => agentToggleVariantIn(b, v.id, e.target.checked))}
                  />
                  <textarea
                    className="agentvartext"
                    rows={2}
                    maxLength={320}
                    defaultValue={v.text}
                    readOnly={!editable}
                    placeholder="Opener text — tokens {name} {address} {me}"
                    onBlur={(e) => { const t = e.target.value; if (editable && t !== v.text) void act((b) => agentEditVariantIn(b, v.id, { text: t })); }}
                  />
                  <select
                    className="agentvarcamp"
                    value={v.camp}
                    disabled={!editable}
                    title="Campaign for this script"
                    onChange={(e) => void act((b) => agentEditVariantIn(b, v.id, { camp: e.target.value }))}
                  >
                    {campOptions.map((c) => <option key={c} value={c}>{c === "default" ? "General" : c}</option>)}
                  </select>
                  <button
                    className="agentvardel"
                    disabled={!editable}
                    title="Delete this variant"
                    onClick={() => void act((b) => agentDelVariantIn(b, v.id))}
                  >
                    ×
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {onTogglePtext ? (
      <div className="dcard">
        <div className="dhead">
          <h2>Personal texter — non-Zillow leads, your number</h2>
          <label className="ntswitch">
            <input type="checkbox" className="ntswitchcb" checked={ptextOn} onChange={(e) => onTogglePtext(e.target.checked)} />
            <span className="ntswitchtrack" aria-hidden="true" /><span className="ntswitchknob" aria-hidden="true" />
          </label>
        </div>
        <p className="sub">
          Status-driven texts from (321) 510-4324 for leads whose source is anything but Zillow —
          the two engines can never text the same lead. Needs Review / Needs Address nudge every
          4 days, Follow Up / Negotiation / Not Interested / Offer Sent every 3, Make Offer only
          alerts you and Daniel until your offer text flips it to Offer Sent automatically.
          A reply resets the clock; texting ends only when the status changes, they text STOP,
          or this switch goes off. Entry fence: leads from the last 3 weeks and everything new —
          old Mark Saba leads never enter. No daily cap (your call, 2026-08-10); quiet hours and
          1–2 min spacing always apply.
        </p>
      </div>
      ) : null}

      <div className={"dcard" + (A.autoOutreachOn ? " liveon" : "")}>
        <div className="dhead">
          <h2>🤖 Auto outreach — text relevant leads automatically</h2>
          <label className="ntswitch">
            <input type="checkbox" className="ntswitchcb" checked={A.autoOutreachOn} onChange={(e) => void act((b) => agentSetAutoOutreachIn(b, e.target.checked))} />
            <span className="ntswitchtrack" aria-hidden="true" /><span className="ntswitchknob" aria-hidden="true" />
          </label>
        </div>
        <p className="sub">
          When ON, the agent SENDS its cold drafts itself — openers and every trigger
          (new keyword hits, days-on-market, price cuts) — no approve tap needed.
          Every guard still applies: Zillow-source leads only, at most 50 texts/day and 5 per
          pass, one cold text per number ever, and 9 AM–8 PM Eastern only. Replies to inbound
          texts already auto-send regardless of this switch. Turn OFF and queued drafts drop
          back to "pending" — they wait for your tap and nothing sends on its own.
          {A.autoOutreachOn ? <><br /><strong>Live now.</strong> The cron is sending intros on its own within the caps above.</> : null}
        </p>
      </div>

      <div className="dcard">
        <div className="dhead">
          <h2>Re-engage older leads</h2>
          <label className="ntswitch">
            <input type="checkbox" className="ntswitchcb" checked={A.reengageOn} onChange={(e) => void act((b) => agentSetReengageIn(b, e.target.checked))} />
            <span className="ntswitchtrack" aria-hidden="true" /><span className="ntswitchknob" aria-hidden="true" />
          </label>
        </div>
        <p className="sub">
          Every cron pass drafts up to 15 texts for Zillow leads older than 48 hours: an opener
          for leads never texted, a follow-up for leads texted 7+ days ago that are still on
          market. Drafts land in the queue as pending and wait for "Send all approved" —
          unless Auto outreach (below) is on, in which case they send on their own within
          the 9 AM–8 PM window. Leads without a phone on file are skipped and counted in the
          cron log.
        </p>
      </div>

      <div className="dcard">
        <div className="dhead">
          <h2>Auto reply — "do you have an agent?"</h2>
          <label className="ntswitch">
            <input type="checkbox" className="ntswitchcb" checked={A.repAuto.on} onChange={(e) => void act((b) => agentSetRepAutoIn(b, { on: e.target.checked }))} />
            <span className="ntswitchtrack" aria-hidden="true" /><span className="ntswitchknob" aria-hidden="true" />
          </label>
        </div>
        <p className="sub">When a reply asks if you're represented, this sends ONCE per contact (9 AM–8 PM ET), flags the thread HOT and notifies you to take over. Every other reply stays manual.</p>
        <textarea className="notes" rows={3} defaultValue={A.repAuto.text} onBlur={(e) => { const v = e.target.value; if (v !== A.repAuto.text) void act((b) => agentSetRepAutoIn(b, { text: v })); }} />
      </div>

      <div className="dcard">
        <div className="dhead">
          <h2>Conversation Agent</h2>
          <label className="ntswitch">
            <input type="checkbox" className="ntswitchcb" checked={A.convAgent.on} onChange={(e) => void act((b) => agentSetConvIn(b, { on: e.target.checked }))} />
            <span className="ntswitchtrack" aria-hidden="true" /><span className="ntswitchknob" aria-hidden="true" />
          </label>
        </div>
        <p className="sub">Drafts contextual replies from the full thread. Offers, meeting times and contract talk always escalate to you (HOT + notification, nothing sent). Max 3 AI sends per thread, then approval only.</p>
        <div className="convchecks">
          {([["needs_info", "Auto send: Asked a question"], ["interested", "Auto send: Positive / keep going"], ["qualified", "Auto send: Gave details"]] as const).map(([kk, label]) => (
            <label key={kk} className="convcheck">
              <input type="checkbox" checked={A.convAgent.auto[kk]} onChange={(e) => void act((b) => agentSetConvIn(b, { auto: { [kk]: e.target.checked } }))} />
              {label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
